import { Globe } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_NAMES, SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/shared/i18n';
import { cn } from '@/shared/lib/cn';

interface LanguageSwitcherProps {
  collapsed?: boolean;
  menuPlacement?: 'up' | 'down';
}

const isSupported = (lng: string): lng is SupportedLanguage =>
  (SUPPORTED_LANGUAGES as readonly string[]).includes(lng);

export const LanguageSwitcher = ({
  collapsed = false,
  menuPlacement = 'up',
}: LanguageSwitcherProps) => {
  const { i18n, t } = useTranslation('app');
  const [open, setOpen] = useState(false);

  const current = isSupported(i18n.language) ? i18n.language : 'en';

  const select = (lng: SupportedLanguage) => {
    setOpen(false);
    void i18n.changeLanguage(lng);
  };

  return (
    <div className="relative w-full">
      {open ? (
        <button
          aria-hidden="true"
          className="fixed inset-0 z-40 cursor-default"
          onClick={() => {
            setOpen(false);
          }}
          tabIndex={-1}
          type="button"
        />
      ) : null}

      <button
        aria-expanded={open}
        aria-label={t('language.switcherLabel')}
        className={cn(
          'no-underline text-sm rounded-xs p-2 transition-colors flex items-center overflow-hidden w-full',
          collapsed ? 'justify-center' : 'gap-2',
          'hover:bg-primary-tint hover:shadow-sm',
        )}
        onClick={() => {
          setOpen((value) => !value);
        }}
        title={collapsed ? t('language.switcherLabel') : undefined}
        type="button"
      >
  
        <Globe aria-hidden="true" className="size-4 shrink-0" />
        <span
          className={cn(
            'overflow-hidden whitespace-nowrap transition-all duration-200',
            collapsed ? 'max-w-0 opacity-0' : 'max-w-xs opacity-100',
          )}
        >
          {LANGUAGE_NAMES[current]}
        </span>
      </button>

      {open ? (
        <div
          className={cn(
            'absolute z-50 grid min-w-36 gap-0.5 rounded-md border border-surface-tint bg-surface p-1 shadow-modal',
            menuPlacement === 'up' ? 'bottom-full left-0 mb-1' : 'top-full right-0 mt-1',
          )}
          role="menu"
        >
          {SUPPORTED_LANGUAGES.map((lng) => (
            <button
              aria-checked={lng === current}
              className={cn(
                'rounded-xs px-2 py-1.5 text-left text-sm hover:bg-primary-tint',
                lng === current ? 'font-semibold text-primary-ink' : 'text-ink',
              )}
              key={lng}
              onClick={() => {
                select(lng);
              }}
              role="menuitemradio"
              type="button"
            >
              {LANGUAGE_NAMES[lng]}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};
